/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { Datepicker, DayPicker, Input, Text, theme } from "@nordcloud/gnui";
import { InlineEditForm } from "~/components/InlineEdit/InlineEditForm";
import { dateFormat } from "~/constants";

const isoDateFormat = "YYYY-MM-DD";

const DatepickerWrapper = styled.div`
  background-color: ${theme.color.background.ui01};
  position: absolute;
  z-index: ${theme.zindex.popup};
  filter: drop-shadow(0 1.5rem 1.5rem rgba(0, 0, 0, 0.2));
`;

type Props = {
  defaultValue?: string | null;
  onSubmit: (value: string | undefined) => Promise<void>;
  canEdit?: boolean;
  clearable?: boolean;
};

function InlineEditDatePicker({
  defaultValue,
  onSubmit,
  canEdit,
  clearable,
}: Props) {
  const [value, setValue] = React.useState(defaultValue);
  const [inputValue, setInputValue] = React.useState(defaultValue);
  const [inputError, setInputError] = React.useState(false);
  const [hidePicker, setHidePicker] = React.useState(false);

  React.useEffect(() => {
    setValue(defaultValue);
  }, [setValue, defaultValue]);

  const setValueFromInput = (newValue: string | undefined) => {
    setInputValue(newValue);

    const newDate = dayjs(newValue);
    setInputError(!newDate.isValid());
    if (newDate.isValid()) {
      setValue(newDate.format(isoDateFormat));
    }
  };

  const display = (val?: string | null) => (
    <Text m="0">{val ? dayjs(val).format(dateFormat) : "–"}</Text>
  );

  const clear = async () => {
    setValue(null);
    setHidePicker(true);
  };

  return (
    <InlineEditForm
      onSubmit={async () => onSubmit(value || undefined)}
      display={display(defaultValue || "")}
      canEdit={canEdit}
      saveDisabled={inputError}
      onEdit={() => setHidePicker(false)}
      onClear={clearable ? clear : undefined}
      editModeOnClear={clearable}
    >
      {hidePicker ? (
        display(value)
      ) : (
        <div>
          <Input
            type="text"
            small
            value={inputValue || undefined}
            status={inputError ? "danger" : undefined}
            onChange={(ev) => setValueFromInput(ev.target.value)}
          />
          <DatepickerWrapper>
            <Datepicker>
              <DayPicker
                mode="single"
                selected={value ? dayjs(value).toDate() : undefined}
                onDayClick={(newD: Date | undefined) => {
                  const newValue = dayjs(newD).format(isoDateFormat);
                  setValue(newValue);
                  setHidePicker(true);
                }}
              />
            </Datepicker>
          </DatepickerWrapper>
        </div>
      )}
    </InlineEditForm>
  );
}

export { InlineEditDatePicker };
